// ImpactReports — admin /reports/impact (CHG-1659).
//
// Spec authority: specs/tier-05-experience/impact-reports.entity-hint.yaml
// (pages.dashboard_v2 block). Documented hand-roll exception per DRIFT-702.

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { DashboardPage } from "../components/composition/DashboardPage";
import {
  StatStrip,
  type StatStripItem,
} from "../components/composition/StatStrip";
import { useFormatters } from "../contexts/IntlContext";
import { reportsApi, type ImpactPeriod, type ReportsImpact } from "../api/reports";

const PERIODS: { value: ImpactPeriod; label: string }[] = [
  { value: "7d", label: "Last 7 days" },
  { value: "30d", label: "Last 30 days" },
  { value: "90d", label: "Last 90 days" },
  { value: "365d", label: "Last 365 days" },
  { value: "ytd", label: "Year to date" },
];

function statsFromImpact(data: ReportsImpact | undefined): StatStripItem[] {
  return [
    {
      id: "total_raised",
      label: "Total raised",
      value: data?.total_raised_minor_units ?? null,
      format: "currency",
      currency: "USD",
      tone: "g-giving",
    },
    {
      id: "total_donations",
      label: "Donations",
      value: data?.total_donations ?? null,
      format: "number",
      tone: "g-giving",
    },
    {
      id: "unique_donors",
      label: "Donors reached",
      value: data?.unique_donors ?? null,
      format: "number",
      tone: "g-people",
    },
    {
      id: "programs_funded",
      label: "Programs funded",
      value: data?.programs_funded ?? null,
      format: "number",
      tone: "g-giving",
    },
    {
      id: "campaigns_goal_met",
      label: "Campaigns at goal",
      value: data?.campaigns_goal_met ?? null,
      format: "number",
      tone: "g-giving",
    },
  ];
}

function PeriodPicker({
  period,
  onChange,
}: {
  period: ImpactPeriod;
  onChange: (period: ImpactPeriod) => void;
}) {
  return (
    <select
      value={period}
      onChange={(e) => onChange(e.target.value as ImpactPeriod)}
      style={{
        height: 32,
        padding: "0 12px",
        borderRadius: 6,
        border: "1px solid var(--color-border)",
        background: "var(--color-surface)",
        color: "var(--color-text)",
        fontSize: 13,
      }}
      aria-label="Reporting period"
    >
      {PERIODS.map((p) => (
        <option key={p.value} value={p.value}>
          {p.label}
        </option>
      ))}
    </select>
  );
}

function CampaignProgressCard({
  data,
}: {
  data: ReportsImpact | undefined;
}) {
  const { formatNumber } = useFormatters();
  if (!data || data.campaigns_total === 0) return null;
  const pct = Math.round((data.campaigns_goal_met / data.campaigns_total) * 100);
  return (
    <section
      className="card"
      aria-labelledby="campaign-progress-title"
      style={{ padding: 16, display: "flex", flexDirection: "column", gap: 8 }}
    >
      <h2
        id="campaign-progress-title"
        style={{
          fontSize: 12,
          fontWeight: 500,
          margin: 0,
          color: "var(--color-text-muted)",
          textTransform: "uppercase",
          letterSpacing: "0.04em",
        }}
      >
        Campaigns at goal
      </h2>
      <div style={{ fontSize: 24, fontWeight: 600 }}>
        {formatNumber(data.campaigns_goal_met)}
        <span
          style={{
            fontSize: 14,
            fontWeight: 400,
            color: "var(--color-text-muted)",
          }}
        >
          {" "}
          / {formatNumber(data.campaigns_total)} ({pct}%)
        </span>
      </div>
      <div
        aria-hidden="true"
        style={{
          height: 6,
          background: "var(--color-border)",
          borderRadius: 3,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            height: "100%",
            width: `${pct}%`,
            background: "var(--accent, #0066cc)",
          }}
        />
      </div>
    </section>
  );
}

export function ImpactReports() {
  const [period, setPeriod] = useState<ImpactPeriod>("365d");
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["reports", "impact", period] as const,
    queryFn: () => reportsApi.fetchReportsImpact(period),
  });
  return (
    <DashboardPage
      title="Impact reports"
      subtitle="Outcomes summary: donations raised, donors reached, programs funded, campaign goals met."
      stats={<StatStrip items={statsFromImpact(data)} loading={isLoading} />}
      feed={
        <>
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              marginBottom: 12,
            }}
          >
            <PeriodPicker period={period} onChange={setPeriod} />
          </div>
          <CampaignProgressCard data={data} />
        </>
      }
      callout={null}
      loading={isLoading}
      error={error as Error | null}
      onRetry={() => refetch()}
    />
  );
}
